const SIZE_PATTERN = /\/\d+x\d+\//;
const VEHICLE_PHOTOS_DIR = /^\/vehicle_photos\//;
const ALLOWED_HOSTS = /\.acvauctions\.com$/;
const ALLOWED_PATHS = /^\/(public|vehicle_photos)\//;

/**
 * Injects a size segment into a CDN photo URL's pathname.
 *
 * Injection rules by domain:
 * - `condition-report-images.acvauctions.com` → prepend `/{size}` to the pathname
 * - `cdn.*.acvauctions.com` / `cdn.acvauctions.com` → insert `/{size}` after `/vehicle_photos/`
 *
 * Returns the original URL unchanged if it doesn't match expected CDN patterns.
 *
 * @param {string} url - The original photo URL from the BFF
 * @param {string} [size='188x188'] - The desired size segment (e.g. '188x188')
 * @returns {string} The URL with the size segment injected, or the original value if falsy/unparseable/unexpected
 *
 * @example
 * injectPhotoSize('https://condition-report-images.acvauctions.com/public/photo_abc.jpg')
 * // => 'https://condition-report-images.acvauctions.com/188x188/public/photo_abc.jpg'
 *
 * injectPhotoSize('https://cdn.acvauctions.com/vehicle_photos/d7b0da27-2cab-4218-b358-81eacf26cd7b.jpg')
 * // => 'https://cdn.acvauctions.com/vehicle_photos/188x188/d7b0da27-2cab-4218-b358-81eacf26cd7b.jpg'
 */
export const injectPhotoSize = (url, size = '188x188') => {
  if (!url) return url;

  try {
    const parsed = new URL(url);

    if (!ALLOWED_HOSTS.test(parsed.hostname)) {
      return url;
    }

    // Strip any existing size segment for idempotency check
    const basePath = parsed.pathname.replace(SIZE_PATTERN, '/');
    if (!ALLOWED_PATHS.test(basePath)) {
      return url;
    }

    // Remove existing size segment if present (for replacement/idempotency)
    const cleanPathname = parsed.pathname.replace(SIZE_PATTERN, '/');

    if (VEHICLE_PHOTOS_DIR.test(cleanPathname)) {
      // cdn.*.acvauctions.com: /vehicle_photos/{size}/filename.jpg
      parsed.pathname = cleanPathname.replace(VEHICLE_PHOTOS_DIR, `/vehicle_photos/${size}/`);
    } else {
      // condition-report-images: /{size}/public/filename.jpg
      parsed.pathname = `/${size}${cleanPathname}`;
    }

    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Transforms `vehicle.photoUrl` on a job object (or array of job objects) in place.
 * Intended to be called at the store level after fetching jobs from the BFF.
 *
 * @param {Object|Object[]} jobs - A single job object or array of job objects
 * @param {string} [size='188x188'] - The desired size segment
 * @returns {Object|Object[]} The same reference(s), mutated with optimized photoUrl
 */
export const optimizeJobPhotoUrls = (jobs, size = '188x188') => {
  if (!jobs) return jobs;

  const optimize = (job) => {
    if (job?.vehicle?.photoUrl) {
      job.vehicle.photoUrl = injectPhotoSize(job.vehicle.photoUrl, size);
    }
    return job;
  };

  return Array.isArray(jobs) ? jobs.map(optimize) : optimize(jobs);
};
